// JS is enabled
document.querySelector('html').classList.add('js');

/**
 * Modals with the native <dialog> element
 *
 * Allows to load content from an external URL when opened,
 * which works well with the Shopify Section Rendering API.
 */
class DialogOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const dialog = document.querySelector(this.getAttribute('data-dialog'));
      if (dialog) dialog.show(button);
    });
  }
}

customElements.define('dialog-opener', DialogOpener);

class DialogModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.content = this.querySelector('.dialog-content');
    this.load = this.getAttribute('data-load');
    this.loaded = false;
    this.loadingClass = 'loading';

    this.dialog.addEventListener('click', this.handleClose.bind(this));
  }

  show() {
    this.dialog.showModal();
    this.loadContent();
  }

  // Close when clicked outside the dialog.
  // <form method="dialog"> used for close button.
  handleClose(event) {
    const x = event.clientX;
    const y = event.clientY;
    const dialog = this.dialog.getBoundingClientRect();

    // X and Y are 0 if keyboard navigation is used,
    // which can result in unwanted closing of modal.
    // The close button will still work with <form>.
    if (x === 0 && y === 0) return;

    if (x < dialog.left || x > dialog.right || y < dialog.top || y > dialog.bottom) {
      this.dialog.close();
    }
  }

  // Load content from URL if data-load attribute is set and not yet loaded
  loadContent() {
    if (this.content && this.load && !this.loaded) {
      this.content.classList.add(this.loadingClass);
      fetch(this.load)
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.text();
        })
        .then((text) => {
          this.content.innerHTML = text;
          this.loaded = true;
        })
        .catch((error) => {
          this.content.innerHTML = 'Failed to load content';
          console.error(error);
        })
        .finally(() => {
          this.content.classList.remove(this.loadingClass);
        });
    }
  }
}

customElements.define('dialog-modal', DialogModal);

/**
 * StickyHeader
 * Shows the header when scrolling up
 */
class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.lastScrollTop = 0;
    this.scrollHistory = [];
    this.scrollDirection = 'down';
    this.logoAnimationTriggered = false; // Flag to prevent animation looping
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.navModal = document.querySelector('nav-modal');
    this.height = this.header.offsetHeight;
    this.hamburgerState = 0; // 0 - closed , 1 - open
    this.dropdownActive = 0;
    this.currentScrollTop = 0;
    this.openedNavItem = null; // desktop hover over link
    this.stuckClass = 'sticky-header-stuck';
    this.hiddenClass = 'sticky-header-hidden';
    // this.cartOpen  = false;
    this.hamburger = document.querySelector('.js-hamburger');

    // Initialize Headroom.js instead of manual scroll handling
    this.initHeadroom();

    this.hamburgerMenu();
    this.handleMenuDropdown();
    this.handleMobileSubmenu();
    this.handleBackToMenu();
    this.handleCloseAll();
    this.handleCartModal();
    this.handleAccountBanner();
    this.handleFormSubmittedBanner();
    this.handleFormValidation();
    this.handleAddToCartForms();

    document.querySelectorAll('.close-dropdown-on-hover').forEach((item) => {
      item.addEventListener('mouseenter', (event) => {
        this.dropdownActive = false;
        this.classList.add('dropdown-deactive');
        this.classList.remove('dropdown-active');
        document.querySelector('body').classList.remove('menu-dropdown-active');
        this.handleCloseAllSubmenus();
        this.openedNavItem = null;
      });
    });
  }

  handleFormSubmittedBanner() {
    const banner = this.querySelector('.js-form-submitted');

    if (!banner) return;

    const url = new URL(window.location.href);
    const contactPosted = url.searchParams.get('contact_posted');
    const hashMatches = url.hash && url.hash.toLowerCase().startsWith('#contact_form');

    if (contactPosted === 'true' && hashMatches) {
      banner.classList.remove('hidden');

      setTimeout(() => {
        banner.classList.add('hidden');
      }, 6000);
    }
  }

  handleFormValidation() {
    const forms = document.querySelectorAll('form'); // Adjust selector if you have a specific form
    const emptyFormBanner = this.querySelector('.js-form-empty-form');

    if (!emptyFormBanner) return;

    forms.forEach((form) => {
      // Skip product forms - they have their own validation logic
      if (
        form.hasAttribute('data-product-form') ||
        (form.hasAttribute('data-type') && form.getAttribute('data-type') === 'add-to-cart-form')
      ) {
        return;
      }

      const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');

      if (!submitButton) return;

      submitButton.addEventListener('click', (event) => {
        // Get all input fields (input, textarea, select)
        const fields = form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]), textarea, select');
        let allFieldsEmpty = true;

        fields.forEach((field) => {
          const value = field.value.trim();
          if (value) {
            allFieldsEmpty = false;
          }
        });

        // If all fields are empty, prevent submission and show banner
        if (allFieldsEmpty) {
          event.preventDefault();

          emptyFormBanner.classList.remove('hidden');

          setTimeout(() => {
            emptyFormBanner.classList.add('hidden');
          }, 4000);
        }
      });
    });
  }

  handleAddToCartForms() {
    const addToCartForms = document.querySelectorAll('form[data-type="add-to-cart-form"]');
    
    addToCartForms.forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        
        const formData = new FormData(form);
        const quantityInput = document.querySelector('.qty-input');
        const quantity = quantityInput ? parseInt(quantityInput.value) : 1;
        
        // Update the quantity in form data
        formData.set('quantity', quantity);
        
        // Get the variant ID
        const variantId = formData.get('id');
        
        if (!variantId) {
          console.error('No variant ID found');
          return;
        }
        
        // Prepare the request body
        const items = [{
          id: parseInt(variantId),
          quantity: quantity
        }];
        
        // Add properties if remove old furniture is checked
        const removeCheckbox = document.querySelector('input[name="properties[remove_old_furniture]"]');
        if (removeCheckbox && removeCheckbox.checked) {
          items[0].properties = {
            'remove_old_furniture': 'true'
          };
        }
        
        // Submit to cart
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items: items }),
        })
        .then((response) => response.json())
        .then((data) => {
          console.log('Added to cart:', data);
          
          // Show success message
          const addedBanner = document.querySelector('.js-added-to-cart');
          if (addedBanner) {
            addedBanner.classList.remove('hidden');
            setTimeout(() => {
              addedBanner.classList.add('hidden');
            }, 2000);
          }
          
          // Refresh the cart modal
          const cartModalElement = document.querySelector('cart-modal');
          if (cartModalElement && typeof cartModalElement.fetchProducts === 'function') {
            cartModalElement.fetchProducts();
          }
          
          // Open the cart modal and set cartOpen flag
          this.cartOpen = true;
          const cartWrapper = document.querySelector('.cart-modal-wrapper');
          if (cartWrapper) {
            cartWrapper.classList.add('active');
            
            // Add mouseleave listener for desktop only (same as hover behavior)
            if (window.innerWidth >= 1280) {
              cartWrapper.addEventListener(
                'mouseleave',
                () => {
                  this.closeCartModal();
                },
                { once: true }
              );
            }
          }
        })
        .catch((error) => {
          console.error('Error adding to cart:', error);
        });
      });
    });
  }

  handleAccountBanner() {
    if (window.location.href.indexOf('account=active') > -1) {
      this.querySelector('.js-account-banner ').classList.remove('hidden');

      setTimeout(() => {
        this.querySelector('.js-account-banner ').classList.add('hidden');
      }, 4000);
    }
  }

  handleCartModal() {
    // Close button via delegation
    document.addEventListener('click', (e) => {
      if (e.target.closest('.js-close-modal')) {
        this.closeCartModal();
      }
    });

    // Add scroll listener to close cart modal and dropdown menus
    let scrollTimeout;
    const handleScroll = () => {
      if (this.cartOpen) {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.closeCartModal();
        }, 100); // Small delay to prevent accidental closes
      }

      // Close dropdown/subnav menu on scroll
      if (this.dropdownActive) {
        this.dropdownActive = false;
        this.classList.add('dropdown-deactive');
        this.classList.remove('dropdown-active');
        document.querySelector('body').classList.remove('menu-dropdown-active');
        this.handleCloseAllSubmenus();
        this.openedNavItem = null;
      }
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    Array.from(document.querySelectorAll('.js-cart-icon')).map((cartBtn) => {
      // Function to open cart modal
      const openCartModal = () => {
        this.cartOpen = true;
        this.fetchProducts();

        document.querySelector('.cart-modal-wrapper').classList.add('active');

        // Add mouseleave listener for desktop only
        if (window.innerWidth >= 1280) {
          const cartWrapper = document.querySelector('.cart-modal-wrapper');
          if (cartWrapper) {
            cartWrapper.addEventListener(
              'mouseleave',
              () => {
                this.closeCartModal();
              },
              { once: true }
            );
            // once:true ensures you don't pile up listeners
          }
        }
      };

      // Function to handle cart icon mouse leave with delay
      const handleCartIconLeave = () => {
        if (window.innerWidth >= 1280 && this.cartOpen) {
          // Add a small delay to allow user to move to cart modal
          setTimeout(() => {
            const cartWrapper = document.querySelector('.cart-modal-wrapper');
            const header = document.querySelector('.section-header');
            const isHoveringCart = cartWrapper && cartWrapper.matches(':hover');
            const isHoveringHeader = header && header.matches(':hover');

            // Only close if not hovering over cart modal or header
            if (!isHoveringCart && !isHoveringHeader) {
              this.closeCartModal();
            }
          }, 200); // Increased delay to 200ms for smoother transition
        }
      };

      // Decide interaction type (hover vs click)
      const handleCartInteraction = () => {
        cartBtn.removeEventListener('mouseenter', openCartModal);
        cartBtn.removeEventListener('click', openCartModal);
        cartBtn.removeEventListener('mouseleave', handleCartIconLeave);

        if (window.innerWidth >= 1280) {
          cartBtn.addEventListener('mouseenter', openCartModal);
          cartBtn.addEventListener('mouseleave', handleCartIconLeave);
        } else {
          cartBtn.addEventListener('click', openCartModal);
        }
      };

      // Initial setup
      handleCartInteraction();

      // Re-setup on resize
      window.addEventListener('resize', handleCartInteraction);
    });
  }

  // Helper method to close cart modal
  closeCartModal() {
    this.cartOpen = false;

    // Add animate-out class for exit animation
    const cartModalWrapper = document.querySelector('.cart-modal-wrapper');
    if (cartModalWrapper) {
      cartModalWrapper.classList.add('animate-out');

      // Remove active class and animate-out class after animation completes
      setTimeout(() => {
        cartModalWrapper.classList.remove('active');
        cartModalWrapper.classList.remove('animate-out');
        document.querySelector('body').style.overflowY = 'visible';
      }, 250); // Match the animation duration (0.25s = 250ms)
    }

    if (this.navModal) {
      this.navModal.style.display = 'none';
    }

    const iconClose = this.querySelector('.icon-close');
    const iconHamburger = this.querySelector('.icon-hamburger');

    if (iconClose) iconClose.style.display = 'none';
    if (iconHamburger) iconHamburger.style.display = 'block';
  }

  formatNumberWithCommas(number) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * Waits for Bold app to be ready before executing callback
   * @param {Function} callback - Function to execute when Bold is ready
   * @param {number} maxWaitTime - Maximum time to wait in milliseconds (default: 10000)
   * @param {number} checkInterval - Interval to check in milliseconds (default: 100)
   */
  waitForBold(callback, maxWaitTime = 10000, checkInterval = 100) {
    const startTime = Date.now();

    const checkBold = () => {
      // Check if Bold is loaded and has the required methods
      if (window.BOLD && window.BOLD.pre && typeof window.BOLD.pre.getCart === 'function') {
        try {
          // Try to access getCart to ensure it's fully initialized
          const cart = window.BOLD.pre.getCart();
          if (cart && cart.items) {
            callback();
            return;
          }
        } catch (e) {
          // Bold might be partially loaded, continue waiting
          console.log('Bold partially loaded, waiting...');
        }
      }

      // Check if we've exceeded max wait time
      if (Date.now() - startTime >= maxWaitTime) {
        console.warn('Bold app did not load within the expected time. Continuing without Bold pricing updates.');
        return;
      }

      // Check again after interval
      setTimeout(checkBold, checkInterval);
    };

    checkBold();
  }

  fetchProducts() {
    fetch(`/cart`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;

        document.querySelector('cart-modal').innerHTML = html.querySelector('cart-modal').innerHTML;
        document.querySelector('.js-mobile-drawer-price').innerText =
          html.querySelector('.js-cart-total-price').innerText;

        this.handleProductQuantity();
        this.handleProductRemove();

        if (document.querySelector('body').dataset.user == 'business') {
          // Wait for Bold to be ready and use discounted prices
          this.waitForBold(() => {
            try {
              const cart = window.BOLD.pre.getCart();

              // Calculate total from items to ensure accuracy
              let calculatedTotal = 0;

              // Update individual item prices with Bold's discounted prices
              cart.items.map((item) => {
                const id = item.id.split(':')[0];
                const priceElement = document.querySelector(
                  `cart-modal .js-cart-item[data-line="${id}"] .js-bold-cart-price`
                );
                if (priceElement) {
                  // Use final_line_price if available (includes discounts and quantity), otherwise calculate
                  let linePrice;

                  // Check for line-level price first (preferred)
                  if (
                    item.final_line_price !== undefined &&
                    item.final_line_price !== null &&
                    item.final_line_price > 0
                  ) {
                    linePrice = item.final_line_price;
                  } else if (item.line_price !== undefined && item.line_price !== null && item.line_price > 0) {
                    linePrice = item.line_price;
                  }
                  // If no line price, calculate from unit price * quantity
                  else if (item.final_price !== undefined && item.final_price !== null) {
                    linePrice = item.final_price * (item.quantity || 1);
                  } else if (item.price !== undefined && item.price !== null) {
                    linePrice = item.price * (item.quantity || 1);
                  } else {
                    console.warn('Could not determine price for item:', item);
                    return; // Skip this item
                  }

                  priceElement.innerText = '£' + this.formatNumberWithCommas(linePrice / 100);

                  // Add to calculated total
                  calculatedTotal += linePrice;
                }
              });

              // Use calculated total from items (most accurate), fallback to cart properties
              const subtotal =
                calculatedTotal > 0 ? calculatedTotal : cart.sub_total || cart.total_price || cart.total || 0;

              const totalPriceElement = document.querySelector('.js-cart-total-price');
              if (totalPriceElement) {
                totalPriceElement.innerText = '£' + this.formatNumberWithCommas(subtotal / 100);
              }

              // Also update cart-subtotal and cart-total if they exist (for checkout pages)
              const cartSubtotalElement = document.getElementById('cart-subtotal');
              if (cartSubtotalElement) {
                cartSubtotalElement.innerText = '£' + this.formatNumberWithCommas(subtotal / 100);
              }

              const cartTotalElement = document.getElementById('cart-total');
              if (cartTotalElement) {
                cartTotalElement.innerText = '£' + this.formatNumberWithCommas(subtotal / 100);
              }

              // Store Bold cart data globally for order submission
              window.boldCartData = cart;
            } catch (error) {
              console.error('Error updating Bold prices:', error);
            }
          });
        }
      });
  }

  handleProductQuantity() {
    Array.from(document.querySelectorAll('.js-minus-quantity')).map((element) => {
      element.addEventListener('click', (event) => {
        let updates = {};
        if (this.getProductQuantity(element) - 1 != 0) {
          updates[element.parentElement.dataset.product] = this.getProductQuantity(element) - 1;

          fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ updates }),
          }).then((resp) => {
            this.fetchProducts();
          });
        }
      });
    });

    Array.from(document.querySelectorAll('.js-plus-quantity')).map((element) => {
      element.addEventListener('click', (event) => {
        let updates = {};
        updates[element.parentElement.dataset.product] = this.getProductQuantity(element) + 1;

        fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        }).then((resp) => {
          this.fetchProducts();
        });
      });
    });
  }

  getProductQuantity(element) {
    return parseInt(element.parentElement.querySelector('.js-quantity-input').value);
  }

  handleProductRemove() {
    Array.from(document.querySelectorAll('.js-remove-product')).map((element) => {
      element.addEventListener('click', (event) => {
        let updates = {};

        updates[element.dataset.product] = 0;

        fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        }).then((resp) => {
          this.fetchProducts();
        });
      });
    });
  }

  handleCloseAll() {
    Array.from(this.querySelectorAll('.js-close-all')).map((btn) => {
      btn.addEventListener('click', () => {
        Array.from(this.querySelectorAll('.nav-modal--submenu')).map((submenu) => {
          submenu.classList.add('hidden');
        });

        this.hamburgerState = !this.hamburgerState;

        if (this.hamburgerState) {
          this.navModal.classList.add('mobile-active');
          document.querySelector('body').style.overflowY = 'hidden';
          this.querySelector('.icon-hamburger').style.display = 'none';
          this.querySelector('.icon-close').style.display = 'block';
        } else {
          this.navModal.classList.remove('mobile-active');
          document.querySelector('body').style.overflowY = 'visible';
          this.querySelector('.icon-close').style.display = 'none';
          this.querySelector('.icon-hamburger').style.display = 'block';
        }
      });
    });
  }

  handleBackToMenu() {
    Array.from(this.querySelectorAll('.js-back-to-menu')).map((btn) => {
      btn.addEventListener('click', () => {
        Array.from(this.querySelectorAll('.nav-modal--submenu')).map((submenu) => {
          submenu.classList.add('hidden');
        });
      });
    });
  }

  handleMobileSubmenu() {
    Array.from(this.querySelectorAll('.js-mobile-links')).map((link) => {
      link.addEventListener('click', (event) => {
        if (link.dataset.target == 'Shop') {
          // Hide all submenus first
          Array.from(this.querySelectorAll('.nav-modal--submenu')).map((submenu) => {
            submenu.classList.add('hidden');
          });
          // Then show the Shop submenu
          Array.from(this.querySelectorAll('.nav-modal--submenu')).map((submenu) => {
            if (submenu.dataset.modal == 'Shop') {
              submenu.classList.remove('hidden');
            }
          });

          event.preventDefault();
          return false;
        }

        if (link.dataset.target == 'Collections') {
          // Hide all submenus first
          Array.from(this.querySelectorAll('.nav-modal--submenu')).map((submenu) => {
            submenu.classList.add('hidden');
          });
          // Then show the Collections submenu
          Array.from(this.querySelectorAll('.nav-modal--submenu')).map((submenu) => {
            if (submenu.dataset.modal == 'Collections') {
              submenu.classList.remove('hidden');
            }
          });

          event.preventDefault();
          return false;
        }

        if (link.dataset.target == 'Services') {
          // Hide all submenus first
          Array.from(this.querySelectorAll('.nav-modal--submenu')).map((submenu) => {
            submenu.classList.add('hidden');
          });
          // Then show the Services submenu
          Array.from(this.querySelectorAll('.nav-modal--submenu')).map((submenu) => {
            if (submenu.dataset.modal == 'Services') {
              submenu.classList.remove('hidden');
            }
          });

          event.preventDefault();
          return false;
        }

        if (link.dataset.target == 'Furniture packages') {
          // Hide all submenus first
          Array.from(this.querySelectorAll('.nav-modal--submenu')).map((submenu) => {
            submenu.classList.add('hidden');
          });
          // Then show the Furniture Packages submenu
          Array.from(this.querySelectorAll('.nav-modal--submenu')).map((submenu) => {
            if (submenu.dataset.modal == 'Furniture Packages') {
              submenu.classList.remove('hidden');
            }
          });

          event.preventDefault();
          return false;
        }
      });
    });
  }

  handleMenuDropdown() {
    Array.from(document.querySelectorAll('.link-hover')).map((item) => {
      item.addEventListener('click', (event) => {
        if (event.target.getAttribute('href') == '#') {
          event.preventDefault();
        }
      });

      item.addEventListener('mouseenter', (event) => {
        if (!this.dropdownActive) {
          setTimeout(() => {
            this.dropdownActive = true;
            document.querySelector('.header--logo').classList.add('menu-active');
            document.querySelector('body').classList.add('menu-dropdown-active');
            this.classList.add('dropdown-active');
            this.classList.remove('dropdown-deactive');
            this.handleCloseAllSubmenus();

            item.parentElement.querySelector('.nav-submenu').classList.add('active');
            item.classList.add('highlight');
            this.openedNavItem = item;

            const maxHeight = item.parentElement.querySelector('.nav-submenu').offsetHeight;
            this.handleSubmenuSize(maxHeight);
          }, 300);
        } else {
          this.dropdownActive = true;

          document.querySelector('body').classList.add('menu-dropdown-active');
          this.classList.add('dropdown-active');
          this.classList.remove('dropdown-deactive');
          this.handleCloseAllSubmenus();

          item.parentElement.querySelector('.nav-submenu').classList.add('active');
          item.classList.add('highlight');
          this.openedNavItem = item;

          const maxHeight = item.parentElement.querySelector('.nav-submenu').offsetHeight;
          this.handleSubmenuSize(maxHeight);
        }
      });
    });

    this.addEventListener('mouseleave', () => {
      this.dropdownActive = false;
      this.classList.add('dropdown-deactive');
      this.classList.remove('dropdown-active');
      document.querySelector('body').classList.remove('menu-dropdown-active');
      document.querySelector('.header--logo').classList.remove('menu-active');
      this.handleCloseAllSubmenus();
      this.openedNavItem = null;
    });
  }

  handleSubmenuSize(maxHeight) {
    this.querySelector('.dropdown-overlay').style.maxHeight = maxHeight + 25 + 'px';
  }

  handleCloseAllSubmenus() {
    if (this.openedNavItem) {
      this.openedNavItem.classList.remove('highlight');
    }
    Array.from(this.querySelectorAll('.nav-submenu')).map((submenu) => {
      submenu.classList.remove('active');
    });

    this.handleSubmenuSize(0);
  }

  hamburgerMenu() {
    this.hamburger.addEventListener('click', () => {
      this.hamburgerState = !this.hamburgerState;

      if (this.hamburgerState) {
        this.navModal.classList.add('mobile-active');
        this.navModal.classList.remove('mobile-removed');
        document.querySelector('body').style.overflowY = 'hidden';
        this.querySelector('.icon-hamburger').style.display = 'none';
        this.querySelector('.icon-close').style.display = 'block';
      } else {
        this.navModal.classList.remove('mobile-active');
        this.navModal.classList.add('mobile-removed');
        document.querySelector('body').style.overflowY = 'visible';
        this.querySelector('.icon-close').style.display = 'none';
        this.querySelector('.icon-hamburger').style.display = 'block';
        document.querySelector('.cart-modal-wrapper').classList.remove('active');
        
        // Hide all open submenus when closing the mobile menu
        Array.from(this.querySelectorAll('.nav-modal--submenu')).forEach((submenu) => {
          submenu.classList.add('hidden');
        });
      }
    });
  }

  initHeadroom() {
    // Only initialize headroom on desktop (screen width >= 1024px)
    if (window.innerWidth < 1024) {
      return;
    }

    // Check if we're on homepage to set different offset
    const currentUrl = window.location.href.split('?')[0];
    const homepageUrl = window.location.origin + '/';

    // Disable headroom on /products/divan page (mimeeq template)
    if (currentUrl.includes('/products/divan')) {
      return;
    }

    const isHomepage = document.body.classList.contains('Home');

    if (isHomepage) {
      // On homepage, delay Headroom initialization until after logo animation
      this.initHomepageAnimation();
      // Initialize Headroom after logo animation completes (0.75s + buffer)
      setTimeout(() => {
        this.initializeHeadroomInstance();
      }, 2000); // 2 seconds to ensure animation is complete
    } else {
      // On other pages, initialize Headroom immediately
      this.initializeHeadroomInstance();
    }
  }

  initializeHeadroomInstance() {
    // Check if we're on homepage to set different offset
    const currentUrl = window.location.href.split('?')[0];
    const homepageUrl = window.location.origin + '/';
    let isHomepage = currentUrl === homepageUrl;

    if (document.querySelector('body').classList.contains('country-sa')) {
      isHomepage = currentUrl === homepageUrl + 'sa' || currentUrl === homepageUrl + 'sa/';
    } else if (document.querySelector('body').classList.contains('country-fr')) {
      isHomepage = currentUrl === homepageUrl + 'fr' || currentUrl === homepageUrl + 'fr/';
    }

    // Initialize Headroom.js with custom options
    this.headroom = new Headroom(this.header, {
      tolerance: {
        up: 10,
        down: 10,
      },
      offset: isHomepage ? 510 : 0, // Only start hiding after 200px on homepage
      classes: {
        initial: 'headroom',
        pinned: 'headroom--pinned',
        unpinned: 'headroom--unpinned',
        top: 'headroom--top',
        notTop: 'headroom--not-top',
        bottom: 'headroom--bottom',
        notBottom: 'headroom--not-bottom',
      },
      onPin: () => {
        this.header.classList.add(this.stuckClass);
        this.header.classList.remove(this.hiddenClass);
      },
      onUnpin: () => {
        this.header.classList.remove(this.stuckClass);
        this.header.classList.add(this.hiddenClass);
      },
      onTop: () => {
        this.header.classList.remove(this.stuckClass);
        this.header.classList.remove(this.hiddenClass);

        // Logo animation is now handled by scroll-driven animation, not Headroom callbacks
      },
      onNotTop: () => {
        // Logo animation is now handled by scroll-driven animation, not Headroom callbacks
      },
    });

    this.headroom.init();
  }

  initHomepageAnimation() {
    const currentUrl = window.location.href.split('?')[0];
    const homepageUrl = window.location.origin + '/';

    // Check if it's the root homepage OR a language-specific homepage
    // const isHomepage = currentUrl === homepageUrl || currentUrl.match(/^https?:\/\/[^\/]+\/[a-z]{2}\/?$/);
    const isHomepage = document.body.classList.contains('Home');

    if (isHomepage) {
      // Ensure ScrollTrigger is loaded and registered with GSAP
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
        console.error('GSAP or ScrollTrigger is not loaded');
        return;
      }

      // Register ScrollTrigger plugin with GSAP (it may have auto-registered, but this ensures it's registered)
      if (gsap.registerPlugin) {
        gsap.registerPlugin(ScrollTrigger);
      }
      const logo = document.querySelector('.kingsley-main-logo-container .site-header__logo svg'); // Your logo element
      const logoContainer = document.querySelector('.kingsley-main-logo-container');
      // Check if logo exists before proceeding
      if (!logo) {
        console.error('Logo element not found');
        return;
      }
      // Set initial state explicitly
      gsap.set(logo, { width: '100%' });
      gsap.set(logoContainer, { marginTop: '0px' });
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: 'body', // Use body as trigger to avoid jitter from moving header
          start: 'top top', // When the top of the trigger enters the viewport
          end: '200px top',
          scrub: 1, // Increased lag for smoother motion
          pin: false,
        },
      });
      tl.to(
        logo,
        {
          width: '220px', // Sets a new width for the shrunk logo
          duration: 1,
          onStart: () => {
            logoContainer.classList.add('is-animating');
          },
          onComplete: () => {
            logoContainer.classList.add('animation-complete');
            logoContainer.classList.remove('is-animating');
          },
          onReverseComplete: () => {
            logoContainer.classList.remove('animation-complete');
          },
        },
        0
      );
      tl.to(
        logoContainer,
        {
          marginTop: '-65px',
          duration: 1,
        },
        0
      ); // Start at the same time as logo animation
    }
  }

  disconnectedCallback() {
    if (this.headroom) {
      this.headroom.destroy();
    }

    // Clean up global homepage scroll listener
    if (window.homepageScrollHandler) {
      window.removeEventListener('scroll', window.homepageScrollHandler);
      window.homepageScrollHandler = null;
    }

    // Reset global flags
    window.homepageAnimationInitialized = false;
    window.logoAnimationTriggered = false;
  }

  reset() {
    this.header.classList.remove(this.stuckClass);
    this.header.classList.remove(this.hiddenClass);
  }

  hide() {
    this.header.classList.add(this.hiddenClass);
    this.header.classList.remove(this.stuckClass);
  }

  show() {
    this.header.classList.remove(this.hiddenClass);
    this.header.classList.add(this.stuckClass);
  }
}

customElements.define('sticky-header', StickyHeader);

/**
 * HeroTab
 * Hero tabs
 */

class HeroTab extends HTMLElement {
  constructor() {
    super();
    this.tabLink = '#';
  }

  connectedCallback() {
    this.imageElements = Array.from(this.querySelectorAll('.js-img-link'));
    const tabElements = Array.from(this.querySelectorAll('.js-tab'));
    const contentElements = Array.from(this.querySelectorAll('.js-hero-tab-content'));
    const sliderTab = Array.from(this.querySelectorAll('.js-slider-tab'));
    const swiperElement = this.querySelector('.swiper');

    this.contentElements = contentElements; // Store for later access

    this.handleTabClick(tabElements, contentElements);
    this.handleCarousel(sliderTab, contentElements, swiperElement);
    this.handleImageClick();
    this.firstLink(tabElements, contentElements);
  }

  firstLink(tabElements, contentElements) {
    // Try to get link from first desktop tab
    if (tabElements.length > 0) {
      this.tabLink = tabElements[0].dataset.link;
    }
    // Fallback: get link from first content element
    else if (contentElements.length > 0) {
      this.tabLink = contentElements[0].dataset.link;
    }
  }

  handleImageClick() {
    this.imageElements.map((imgElement) => {
      imgElement.addEventListener('click', () => {
        window.location.href = this.tabLink;
      });
    });

    // Handle the mobile button link
    const mobileBtn = this.querySelector('.btn');
    if (mobileBtn) {
      mobileBtn.addEventListener('click', (e) => {
        e.preventDefault();
        window.location.href = this.tabLink;
      });
    }
  }

  handleTabClick(tabElements, contentElements) {
    tabElements.map((tabItem, index) => {
      tabItem.addEventListener('click', () => {
        window.location.href = this.tabLink;
      });

      tabItem.addEventListener('mouseenter', () => {
        this.tabLink = tabItem.dataset.link;

        tabElements.map((tabElement) => {
          tabElement.classList.remove('hero-tab--active');
        });

        tabItem.classList.add('hero-tab--active');

        contentElements.map((contentItem, contentIndex) => {
          if (contentIndex == index) {
            // Add fade-in transition
            contentItem.style.transition = 'opacity 0.3s ease-in';
            contentItem.style.opacity = '0';
            contentItem.classList.add('hero-content--active');

            // Trigger fade-in after a brief delay
            setTimeout(() => {
              contentItem.style.opacity = '1';
            }, 10);
          } else {
            contentItem.classList.remove('hero-content--active');
            contentItem.style.opacity = '';
            contentItem.style.transition = '';
          }
        });
      });
    });
  }

  handleCarousel(sliderTab, contentElements, swiperElement) {
    const swiper = new Swiper(swiperElement, {
      // Optional parameters
      loop: true,
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 8,
      breakpoints: {
        320: {
          slidesPerView: 'auto',
        },
        768: {
          slidesPerView: 'auto',
          gap: 30,
        },
        1024: {
          slidesPerView: 3,
          gap: 30,
        },
      },
    });

    // Handle slider tab clicks to navigate
    sliderTab.forEach((slider) => {
      slider.addEventListener('click', (e) => {
        e.stopPropagation();
        window.location.href = this.tabLink;
      });
    });

    swiper.on('transitionEnd', () => {
      sliderTab.map((slider) => {
        slider.classList.remove('hero-tab--active');
      });

      sliderTab[swiper.realIndex].classList.add('hero-tab--active');

      contentElements.map((contentItem, contentIndex) => {
        if (contentIndex == swiper.realIndex) {
          contentItem.classList.add('hero-content--active');
          // Update the tab link to match the active content
          this.tabLink = contentItem.dataset.link;
        } else {
          contentItem.classList.remove('hero-content--active');
        }
      });
    });
  }
}

customElements.define('hero-tabs', HeroTab);

class SliderSection extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.prevHover = null;
    this.boundHandlers = new Map(); // Store bound handlers for each slide
    this.boundHoverHandlers = new Map(); // Store bound handlers for each slide
  }

  connectedCallback() {
    const swiperElement = this.querySelector('.swiper');
    this.handleCarousel(swiperElement);
  }

  handleCursorIconShow(hoverCategory) {
    Array.from(document.querySelectorAll('.cursor-icon')).map((icon) => {
      var category = icon.dataset.cursorCategory;

      if (category == hoverCategory) {
        icon.classList.remove('hidden');
      } else {
        icon.classList.add('hidden');
      }
    });
  }

  handleCarouselClick(slide) {
    if (Array.from(slide.classList).indexOf('swiper-slide-active') > -1) {
      if (slide.querySelector('.video-wrapper').getAttribute('href') != '' || slide.querySelector('img').dataset.link) {
        window.location = slide.querySelector('img').dataset.link;
      }
    }

    if (Array.from(slide.classList).indexOf('swiper-slide-next') > -1) {
      this.swiper.slideNext();
    }

    if (Array.from(slide.classList).indexOf('swiper-slide-prev') > -1) {
      this.swiper.slidePrev();
    }
  }

  handleCarouselHover(slide) {
    if (Array.from(slide.classList).indexOf('swiper-slide-next') > -1) {
      slide.querySelector('img').dataset.followerIcon = 'next';
      slide.querySelector('img').dataset.followerText = '';
      this.handleCursorIconShow('next');
    }

    if (Array.from(slide.classList).indexOf('swiper-slide-prev') > -1) {
      slide.querySelector('img').dataset.followerIcon = 'prev';
      slide.querySelector('img').dataset.followerText = '';
      this.handleCursorIconShow('prev');
    }

    if (Array.from(slide.classList).indexOf('swiper-slide-active') > -1) {
      if (
        (slide.querySelector('.video-wrapper') && slide.querySelector('.video-wrapper').getAttribute('href') != '') ||
        slide.querySelector('img').dataset.link
      ) {
        slide.querySelector('img').dataset.followerIcon = 'product';
        slide.querySelector('img').dataset.followerText = 'View Products';
        slide.classList.add('followerchangetest');
        if (slide.querySelector('a')) {
          slide.querySelector('a').style.cursor = 'none';
        }
      } else {
        slide.style.cursor = 'auto';
        slide.querySelector('img').dataset.followerIcon = '';
        slide.querySelector('img').dataset.followerText = '';
        slide.classList.remove('followerchangetest');
      }
    }

    this.prevHover = slide;
  }

  handleCarousel(swiperElement) {
    this.swiper = new Swiper(swiperElement, {
      // Optional parameters
      loop: true,
      slidesPerView: 1.2,
      centeredSlides: false,
      preventInteractionOnTransition: true,
      init: false,
      spaceBetween: 10,
      navigation: {
        nextEl: '.shop-right',
        prevEl: '.shop-left',
      },
      breakpoints: {
        768: {
          centeredSlides: true,
          slidesPerView: 1.6,
        },
        1441: {
          centeredSlides: true,
          slidesPerView: 2.2,
        },
      },
    });

    // FIX THE SWIPER LOOP BUG
    // https://github.com/nolimits4web/swiper/issues/7239
    // Find all slides
    const slides = this.querySelectorAll('.swiper-slide');

    // Clone all slides, add a class, and put them at the end
    slides.forEach((slide) => {
      const clonedSlide = slide.cloneNode(true); // Copy the slide
      clonedSlide.classList.add('swiper-slide-duplicated'); // Add the class
      this.querySelector('.swiper-wrapper').appendChild(clonedSlide); // Add the clone
    });

    this.swiper.on('beforeSlideChangeStart', () => {
      this.swiper.slides.forEach((slide) => {
        if (this.boundHoverHandlers.has(slide)) {
          const boundHandler = this.boundHoverHandlers.get(slide);
          slide.removeEventListener('mouseenter', boundHandler);
        }
      });
    });

    this.swiper.on('transitionEnd', () => {
      this.swiper.slides.forEach((slide) => {
        // Remove the existing event listener (if any)
        if (this.boundHandlers.has(slide)) {
          const boundHandler = this.boundHandlers.get(slide);
          slide.removeEventListener('click', boundHandler);
        }

        const boundHandler = this.handleCarouselClick.bind(this, slide);
        const boundHoverHandler = this.handleCarouselHover.bind(this, slide);

        slide.addEventListener('click', boundHandler);
        slide.addEventListener('mouseenter', boundHoverHandler);

        this.boundHandlers.set(slide, boundHandler);
        this.boundHoverHandlers.set(slide, boundHoverHandler);
      });

      this.swiper.slides.forEach((slide) => {
        slide.style.pointerEvents = 'all';
      });
    });

    this.swiper.init();
  }
}

customElements.define('slider-section', SliderSection);

class TeamSliderSection extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.prevHover = null;
    this.boundHandlers = new Map(); // Store bound handlers for each slide
    this.boundHoverHandlers = new Map(); // Store bound handlers for each slide
    this.nextArrow = this.querySelector('.arrow-right');
    this.prevArrow = this.querySelector('.arrow-left');
  }

  connectedCallback() {
    const swiperElement = this.querySelector('.swiper');
    this.handleCarousel(swiperElement);
  }

  handleCursorIconShow(hoverCategory) {
    Array.from(document.querySelectorAll('.cursor-icon')).map((icon) => {
      var category = icon.dataset.cursorCategory;

      if (category == hoverCategory) {
        icon.classList.remove('hidden');
      } else {
        icon.classList.add('hidden');
      }
    });
  }

  handleCarouselClick(slide) {
    if (Array.from(slide.classList).indexOf('swiper-slide-active') > -1) {
      window.location = slide.querySelector('img').dataset.link;
    }

    if (Array.from(slide.classList).indexOf('swiper-slide-next') > -1) {
      this.swiper.slideNext();
    }

    if (Array.from(slide.classList).indexOf('swiper-slide-prev') > -1) {
      this.swiper.slidePrev();
    }
  }

  handleCarouselHover(slide) {
    if (Array.from(slide.classList).indexOf('swiper-slide-next') > -1) {
      slide.querySelector('img').dataset.followerIcon = 'next';
      slide.querySelector('img').dataset.followerText = '';
      this.handleCursorIconShow('next');
    }

    if (Array.from(slide.classList).indexOf('swiper-slide-prev') > -1) {
      slide.querySelector('img').dataset.followerIcon = 'prev';
      slide.querySelector('img').dataset.followerText = '';
      this.handleCursorIconShow('prev');
    }

    if (Array.from(slide.classList).indexOf('swiper-slide-active') > -1) {
      slide.querySelector('img').dataset.followerIcon = '';
      slide.querySelector('img').dataset.followerText = '';
    }

    this.prevHover = slide;
  }

  handleCarousel(swiperElement) {
    let slidesToShow = 2.6;
    const sliderParent = document.querySelector('.team-slider');
    if (sliderParent.classList.contains('large-slides')) {
      slidesToShow = 1.5;
    }

    // Count slides to determine if loop should be enabled
    const slides = this.querySelectorAll('.swiper-slide');
    const slideCount = slides.length;
    // Loop needs at least 4-5 slides to work properly without blank slides
    // (max slidesPerView is 3 at breakpoint 768)
    const shouldLoop = slideCount >= 5;

    console.log(shouldLoop);

    this.swiper = new Swiper(swiperElement, {
      // Optional parameters
      loop: shouldLoop,
      loopFillGroupWithBlank: false,
      slidesPerView: 2,
      slidesPerGroup: 2,
      centeredSlides: false,
      preventInteractionOnTransition: true,
      init: false,
      draggable: true,
      allowTouchMove: true, // enable drag/touch on small screens
      simulateTouch: true,
      navigation: false,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      breakpoints: {
        768: {
          centeredSlides: true,
          slidesPerView: 3,
          slidesPerGroup: 3,
          slidesPerGroup: 1,
          spaceBetween: 16,
          navigation: {
            nextEl: '.team-right',
            prevEl: '.team-left',
          },
        },
      },
    });

    this.swiper.on('beforeSlideChangeStart', () => {
      this.swiper.slides.forEach((slide) => {
        if (this.boundHoverHandlers.has(slide)) {
          const boundHandler = this.boundHoverHandlers.get(slide);
          slide.removeEventListener('mouseenter', boundHandler);
        }
      });
    });

    // Hide blank slides function
    const hideBlankSlides = () => {
      const blankSlides = this.querySelectorAll('.swiper-slide-blank');
      blankSlides.forEach((blankSlide) => {
        blankSlide.style.display = 'none';
      });
    };

    this.swiper.on('transitionEnd', () => {
      // Hide blank slides after transition
      hideBlankSlides();

      this.swiper.slides.forEach((slide) => {
        // Remove previously-attached click listeners (if any)
        if (this.boundHandlers.has(slide)) {
          const boundHandler = this.boundHandlers.get(slide);
          slide.removeEventListener('click', boundHandler);
        }

        if (this.boundHoverHandlers.has(slide)) {
          const boundHover = this.boundHoverHandlers.get(slide);
          slide.removeEventListener('mouseenter', boundHover);
        }

        // Hover handler (always attach so follower text shows)
        const boundHoverHandler = this.handleCarouselHover.bind(this, slide);
        slide.addEventListener('mouseenter', boundHoverHandler);
        this.boundHoverHandlers.set(slide, boundHoverHandler);

        // Prevent clicks from doing anything but KEEP hover working
        const preventClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
        };
        slide.addEventListener('click', preventClick);
        this.boundHandlers.set(slide, preventClick);
        // optional: add aria so screen readers know it's not interactive
        slide.setAttribute('aria-disabled', 'true');
      });

      this.swiper.slides.forEach((slide) => {
        slide.style.pointerEvents = 'all';
      });
    });

    // Hide blank slides after initialization
    this.swiper.on('init', () => {
      hideBlankSlides();
    });

    // Also hide blank slides on slide change
    this.swiper.on('slideChange', () => {
      hideBlankSlides();
    });

    this.swiper.init();

    // Hide blank slides immediately after init (in case they appear)
    setTimeout(() => {
      hideBlankSlides();
    }, 100);

    // Use MutationObserver to continuously hide blank slides as they appear
    const observer = new MutationObserver(() => {
      hideBlankSlides();
    });

    const wrapper = this.querySelector('.swiper-wrapper');
    if (wrapper) {
      observer.observe(wrapper, {
        childList: true,
        subtree: true,
      });
    }
  }
}

customElements.define('team-slider-section', TeamSliderSection);

class CollectionSection extends HTMLElement {
  constructor() {
    super();
    this.zIndexClass = 'z-40';
    const swiperElement = this.querySelector('.swiper');
    this.hoverLink = '';
    this.hoverElements = Array.from(this.querySelectorAll('.js-action-hover'));
    this.imageElements = Array.from(this.querySelectorAll('.js-target-hover'));

    // Remove desktop navigation on large screens
    if (window.innerWidth > 1024) {
      const swiperNav = this.querySelector('.swiper-navigation');
      if (swiperNav) {
        swiperNav.remove();
      }
    }

    this.handleCarousel(swiperElement);
    this.handleHover();

    this.querySelector('.js-handle-click').addEventListener('click', () => {
      if (window.innerWidth > 1024) {
        window.location = this.hoverLink;
      }
    });
  }

  handleImageChange(index) {
    this.imageElements.map((img, imgIndex) => {
      if (imgIndex == index) {
        img.style.display = 'block';
        this.hoverLink = img.dataset.link;
      } else {
        img.style.display = 'none';
      }
    });
  }

  handleHover() {
    this.hoverElements.map((item, index) => {
      item.addEventListener('mouseenter', (hoverElement) => {
        this.hoverElements.map((itemClass) => itemClass.classList.remove(this.zIndexClass));
        item.classList.add(this.zIndexClass);
        this.handleImageChange(index);
      });
    });
  }

  handleCarousel(swiperElement) {
    const isMobile = window.innerWidth <= 1024;

    const swiper = new Swiper(swiperElement, {
      loop: true,
      slidesPerView: 1,
      centeredSlides: false,
      init: false,
      // Enable swiping on mobile, disable on desktop
      allowTouchMove: isMobile,
      noSwiping: !isMobile,
      // Allow swiping on all elements including text
      noSwipingClass: 'swiper-no-swiping-disabled',
      touchEventsTarget: 'wrapper',
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      navigation: isMobile
        ? {
            nextEl: '.swiper-navigation .slider-mobile-arrows:first-child',
            prevEl: '.swiper-navigation .slider-mobile-arrows:last-child',
          }
        : {
            nextEl: '.js-slider-next',
            prevEl: '.js-slider-prev',
          },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
    });

    swiper.init();
  }
}

customElements.define('collection-section', CollectionSection);

class SliderSimple extends HTMLElement {
  constructor() {
    super();
    this.zIndexClass = 'z-40';
    const swiperElement = this.querySelector('.slider-simple-swiper');
    this.hoverLink = '';
    this.hoverElements = Array.from(this.querySelectorAll('.js-action-hover'));
    this.imageElements = Array.from(this.querySelectorAll('.js-target-hover'));

    this.nextArrow = this.querySelector('.slider-simple-right');
    this.prevArrow = this.querySelector('.slider-simple-left');

    this.handleCarousel(swiperElement);

    this.querySelector('.js-handle-click')?.addEventListener('click', () => {
      if (window.innerWidth > 1024) {
        window.location = this.hoverLink;
      }
    });
  }

  handleCarousel(swiperElement) {
    const isMobile = window.innerWidth <= 1024;

    const swiper = new Swiper(swiperElement, {
      loop: true,
      slidesPerView: 1,
      centeredSlides: false,
      init: false,
      // allowTouchMove: isMobile,
      // noSwiping: !isMobile,
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      navigation: isMobile
        ? {
            nextEl: '.swiper-navigation .slider-mobile-arrows:first-child',
            prevEl: '.swiper-navigation .slider-mobile-arrows:last-child',
          }
        : {
            nextEl: this.nextArrow,
            prevEl: this.prevArrow,
          },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      on: {
        init: () => this.updateNavigation(swiper),
        slideChange: () => this.updateNavigation(swiper),
      },
    });

    swiper.init();
  }

  updateNavigation(swiper) {
    // if (swiper.isBeginning) {
    //   this.prevArrow.style.display = "none";
    // } else {
    //   this.prevArrow.style.display = "block";
    // }
    // if (swiper.isEnd) {
    //   this.nextArrow.style.display = "none";
    // } else {
    //   this.nextArrow.style.display = "block";
    // }
  }
}

customElements.define('slider-simple', SliderSimple);

class SliderMarquee extends HTMLElement {
  constructor() {
    super();
    const swiperElement = this.querySelector('.swiper-img');
    const labelElements = this.querySelector('.swiper-label');

    this.swiperLabel = null;

    this.handleLabelCarousel(labelElements);
    this.handleCarousel(swiperElement);
  }

  handleLabelCarousel(labelElements) {
    this.swiperLabel = new Swiper(labelElements, {
      // Optional parameters
      loop: true,
      slidesPerView: 1.2,
      centeredSlides: true,
      init: false,
      noSwiping: true,
      speed: 1000,
      breakpoints: {
        768: {
          slidesPerView: 1.8,
        },
        1024: {
          slidesPerView: 2.2,
        },
        1280: {
          slidesPerView: 3.2,
        },
        1281: {
          slidesPerView: 3.8,
        },
        1600: {
          slidesPerView: 4.5,
        },
        1921: {
          slidesPerView: 6,
        },
      },
    });

    // FIX THE SWIPER LOOP BUG
    // https://github.com/nolimits4web/swiper/issues/7239
    // Find all slides
    const slides = this.querySelectorAll('.swiper-label .swiper-slide');

    // clone all slides, add a class and put them at the end
    slides.forEach((slide) => {
      const clonedSlide = slide.cloneNode(true); // copy the slide
      clonedSlide.classList.add('swiper-slide-duplicated'); // add the class
      this.querySelector('.swiper-label .swiper-wrapper').appendChild(clonedSlide); // add the clone
    });

    this.swiperLabel.init();
  }

  handleCarousel(swiperElement) {
    const swiper = new Swiper(swiperElement, {
      // Optional parameters
      loop: true,
      slidesPerView: 1,
      centeredSlides: true,
      init: false,
      spaceBetween: 16,
      speed: 1000,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      breakpoints: {
        786: {
          slidesPerView: 1.5,
        },
      },
    });

    // FIX THE SWIPER LOOP BUG
    // https://github.com/nolimits4web/swiper/issues/7239
    // Find all slides
    const slides = this.querySelectorAll('.swiper-img .swiper-slide');

    swiper.on('slideChange', () => {
      if (swiper.touches.diff < 0) {
        this.swiperLabel.slideNext();
      } else {
        this.swiperLabel.slidePrev();
      }
    });

    slides.forEach((slide) => {
      const clonedSlide = slide.cloneNode(true); // copy the slide
      clonedSlide.classList.add('swiper-slide-duplicated'); // add the class
      this.querySelector('.swiper-img .swiper-wrapper').appendChild(clonedSlide); // add the clone
    });

    swiper.init();
  }
}

customElements.define('section-slider-marquee', SliderMarquee);

window.customHandleCursorEffect = function () {
  // Remove any existing event listeners first to prevent duplicates
  document.querySelectorAll('.cursor-hover-effect').forEach((container) => {
    container.removeEventListener('mouseenter', handleCursorEnter);
    container.removeEventListener('mouseleave', handleCursorLeave);
  });

  // Add event listeners to all cursor hover elements
  document.querySelectorAll('.cursor-hover-effect').forEach((container) => {
    container.addEventListener('mouseenter', handleCursorEnter);
    container.addEventListener('mouseleave', handleCursorLeave);
  });

  function handleCursorEnter() {
    var parentElementClass = Array.from(this.parentElement.parentElement.classList);

    if (parentElementClass.includes('swiper-slide') && !parentElementClass.includes('swiper-slide-active')) {
      document.querySelector('.cursor-overlay').style.opacity = 0;
    } else {
      document.querySelector('.cursor-overlay').style.opacity = 1;
    }

    document.querySelector('.cursor-overlay').style.opacity = 1;

    if (this.dataset.cursorTitle) {
      document.querySelector('.cursor-overlay .cursor span').innerText = this.dataset.cursorTitle;
    } else {
      document.querySelector('.cursor-overlay .cursor span').innerText = '';
    }

    Array.from(document.querySelectorAll('.cursor-icon')).map((icon) => {
      var category = icon.dataset.cursorCategory;

      if (category == this.dataset.cursorCategory) {
        icon.classList.remove('hidden');
      } else {
        icon.classList.add('hidden');
      }
    });
  }

  function handleCursorLeave() {
    document.querySelector('.cursor-overlay').style.opacity = 0;
  }
};

class filterContainer extends HTMLElement {
  constructor() {
    super();
    this.activeFilters = {
      category: [],
      rooms: [],
      collections: [],
      size: [],
    };

    this.paramURL = '';
    this.availableCategories = [];
    this.collection = 'all';

    this.openFilterBox = false;

    this.prevCategoryValue = '';
    this.currentSort = 'manual'; // Default sort
    this.currentPage = 1;
    this.handlePaginationClick = this.handlePaginationClick.bind(this);
    this.addEventListener('click', this.handlePaginationClick);
    this.handleFilterSelect();
    this.handleSortSelect();
  }

  connectedCallback() {
    // Set collection from current URL if not already set
    if (this.collection === 'all') {
      const urlPath = window.location.pathname;
      const collectionMatch = urlPath.match(/\/collections\/([^\/]+)/);
      if (collectionMatch && collectionMatch[1] !== 'all') {
        this.collection = collectionMatch[1];
      }
    }

    this.fetchFilters();
    this.applyFilterMobile();
    this.toggleFilterModal();
    this.handleUrlFilters();

    // Set active category based on current collection
    this.setInitialCategoryFilter();

    this.querySelector('.js-clear-all').addEventListener('click', () => {
      this.resetAllFilters();
    });

    this.updateCurrentPageFromDom();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handlePaginationClick);
  }

  handleOpenFilterBox() {
    let previousActiveFilterBox = null;

    Array.from(this.querySelectorAll('.js-open-filter-box')).map((item) => {
      item.addEventListener('click', () => {
        // clicking on the same filter box, closes it
        if (previousActiveFilterBox) {
          previousActiveFilterBox.classList.remove('active');
          previousActiveFilterBox.querySelector('.filter-box-content').classList.remove('active');
        }

        if (previousActiveFilterBox == item.parentElement) {
          previousActiveFilterBox.classList.remove('active');
          previousActiveFilterBox.querySelector('.filter-box-content').classList.remove('active');
          previousActiveFilterBox = null;
          return;
        }

        item.parentElement.classList.add('active');
        item.parentElement.querySelector('.filter-box-content').classList.add('active');

        previousActiveFilterBox = item.parentElement;
      });
    });
  }

  setInitialCategoryFilter() {
    const collectionTitle = this.dataset.collection;
    if (!collectionTitle) return;

    // Wait for filters to be rendered
    setTimeout(() => {
      const normalizedTitle = collectionTitle
        .replace(/-/g, ' ')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      const targetFilter = this.querySelector(`[data-category="Category"][data-value="${normalizedTitle}"]`);
      if (targetFilter) {
        // Remove active from all category filters first
        this.querySelectorAll('[data-category="Category"]').forEach((filter) => {
          filter.classList.remove('active-filter');
          const checkbox = filter.querySelector('input[type="checkbox"]');
          if (checkbox) checkbox.checked = false;
        });

        // Set this one as active
        targetFilter.classList.add('active-filter');
        const checkbox = targetFilter.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = true;

        // Update internal state
        this.activeFilters.category = [normalizedTitle];

        // UPDATE: Call handleFilterSelected to update the visible label
        this.handleFilterSelected('category', normalizedTitle);
      }

      console.log(this.activeFilters);
    }, 1500);
  }

  toggleFilterModal() {
    // this.querySelector('.js-filter-modal').classList.add('hidden');
    const sectionFilters = document.querySelector('section-filters');
    const body = document.querySelector('body');

    this.querySelector('.js-toggle-filter-modal').addEventListener('click', () => {
      this.querySelector('.js-toggle-filter-modal').classList.toggle('active');
      this.querySelector('.filter-wrap').classList.toggle('active');

      if (
        sectionFilters &&
        window.innerWidth < 1024 &&
        !body.classList.contains('Club-Luxe') &&
        !body.classList.contains('Club Luxe') &&
        !body.classList.contains('Urban-Vibe') &&
        !body.classList.contains('Urban Vibe') &&
        !body.classList.contains('Design-Studio') &&
        !body.classList.contains('Design Studio')
      ) {
        document.querySelector('body').classList.toggle('overflow-y-hidden');
      }
    });
  }

  applyFilterMobile() {
    this.querySelector('.js-apply-filters-mobile').addEventListener('click', () => {
      this.fetchAndReplaceHtml(true, true);
      // Hide the mobile filter modal
      this.querySelector('.js-toggle-filter-modal').classList.remove('active');
      this.querySelector('.filter-wrap').classList.remove('active');
      document.querySelector('body').classList.remove('overflow-y-hidden');
    });
  }

  availableCategoryFilters(categoryFilters) {
    // Always keep category filters visible to avoid unexpectedly hiding options
    Array.from(this.querySelectorAll('#Category .js-filter')).map((filterElement) => {
      filterElement.classList.remove('hidden');
    });
  }

  handleFilterSelect() {
    Array.from(this.querySelectorAll('.js-filter')).map((item) => {
      item.addEventListener('click', () => {
        const category = item.dataset.category.toLowerCase();

        // All selected , empty filter group
        if (item.dataset.value == 'All') {
          this.activeFilters[category] = [];
        } else {
          this.filtersPush(category, item.dataset.value);
        }

        // Handle Room filter - show/hide Category filters
        if (category == 'rooms') {
          const categoryFilters = this.querySelectorAll('.js-filter[data-category="Category"]');

          // Reset category to "All" if a specific room is being selected
          if (item.dataset.value !== 'All') {
            // Clear the category active filters
            this.activeFilters['category'] = [];

            // Remove active-filter class from all category filters except "All"
            categoryFilters.forEach(filter => {
              if (filter.dataset.value === 'All') {
                filter.classList.add('active-filter');
              } else {
                filter.classList.remove('active-filter');
              }
            });

            // Update the category filter box selected text
            const categoryFilterSelected = this.querySelector('.filter-box-selected[data-category="Category"]');
            if (categoryFilterSelected) {
              categoryFilterSelected.textContent = 'All';
            }
          }

          if (item.dataset.value == 'All') {
            // Show all category filters when "All" is selected in Rooms
            categoryFilters.forEach(filter => {
              filter.style.display = '';
            });
          } else {
            // Hide category filters that don't match the selected room
            const selectedRoom = item.dataset.value;

            categoryFilters.forEach(filter => {
              // Always keep "All" category visible
              if (filter.dataset.value == 'All') {
                filter.style.display = '';
              } else {
                // Hide if room doesn't match
                const filterRoom = filter.dataset.room;
                if (filterRoom && filterRoom.toLowerCase() === selectedRoom.toLowerCase()) {
                  filter.style.display = '';
                } else {
                  filter.style.display = 'none';
                }
              }
            });
          }
        }

        const hasActive = Array.from(item.classList).indexOf('active-filter') > -1;

        if (category == 'category') {
          if (!hasActive) {
            item.classList.add('active-filter');
          }
        } else {
          item.classList.add('active-filter');
        }

        this.removeFilters(category, item.dataset.value, hasActive);

        if (window.innerWidth > 1024) {
          this.fetchAndReplaceHtml(category != 'category', true);
        }
        this.handleFilterSelected(category, item.dataset.value);
      });
    });
  }

  handleFilterSelected(category, value) {
    Array.from(this.querySelectorAll('.filter-box-selected')).map((item) => {
      if (item.dataset.category.toLowerCase() == category) {
        // For category filters, show count if multiple are selected
        if (category === 'category') {
          const activeCategoryFilters = this.activeFilters.category.filter((filter) => filter !== 'All');
          if (activeCategoryFilters.length === 0) {
            item.innerText = 'All';
          } else if (activeCategoryFilters.length === 1) {
            item.innerText = activeCategoryFilters[0];
          } else {
            item.innerText = `${activeCategoryFilters.length} Selected`;
          }
        } else {
          // For other filters, show the selected value
          item.innerText = value;
        }
      }
    });
  }

  handleSortSelect() {
    Array.from(this.querySelectorAll('.js-sort-filter')).map((item) => {
      item.addEventListener('click', () => {
        // Remove active class from all sort options
        Array.from(this.querySelectorAll('.js-sort-filter')).forEach((sortItem) => {
          sortItem.classList.remove('active-filter');
          const checkbox = sortItem.querySelector('input[type="checkbox"]');
          if (checkbox) checkbox.checked = false;
        });

        // Add active class to clicked option
        item.classList.add('active-filter');
        const checkbox = item.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = true;

        // Update current sort
        this.currentSort = item.dataset.sort;

        // Update the selected text
        const sortSelected = this.querySelector('[data-category="Sort"]');
        if (sortSelected) {
          sortSelected.innerText = item.dataset.label;
        }

        // Apply the sort
        this.applySort();
      });
    });
  }

  applySort() {
    // Apply the current sort by fetching with sort parameter
    this.fetchAndReplaceHtml(false, true);
  }

  filtersPush(category, value) {
    //handle multiple selects on Category
    if (category == 'category') {
      if (this.activeFilters[category].indexOf(value) > -1) {
        this.activeFilters[category] = this.activeFilters[category].filter((filterValue) => filterValue != value);
      } else {
        this.activeFilters[category].push(value);
      }
    } else {
      if (this.activeFilters[category].length) {
        this.activeFilters[category] = [value];
      } else {
        this.activeFilters[category].push(value);
      }
    }
  }

  removeFilters(category, value, clickedOnSameValue) {
    Array.from(this.querySelectorAll('.js-filter')).map((item) => {
      // handle multiple selects on Category group
      if (item.dataset.category.toLowerCase() == 'category') {
        if (category == 'category') {
          if (value == 'All') {
            if (item.dataset.value != 'All') {
              item.classList.remove('active-filter');
            }
          } else {
            if (item.dataset.value == 'All') {
              item.classList.remove('active-filter');
            }

            if (value == item.dataset.value && clickedOnSameValue) {
              item.classList.remove('active-filter');
            }

            if (this.activeFilters['category'].length === 0 && item.dataset.value == 'All') {
              item.classList.add('active-filter');
            }
          }
        }
      } else {
        if (item.dataset.category.toLowerCase() == category) {
          if (item.dataset.value != value) {
            item.classList.remove('active-filter');
          }
        }
      }
    });
  }

  resetAllFilters() {
    // Reset all filter elements
    Array.from(this.querySelectorAll('.js-filter')).map((filterEl) => {
      if (filterEl.dataset.value != 'All') {
        filterEl.classList.remove('active-filter');
        const checkbox = filterEl.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = false;
      } else {
        filterEl.classList.add('active-filter');
        const checkbox = filterEl.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = true;
      }
    });

    // Reset all filter labels to "All"
    Array.from(this.querySelectorAll('.filter-box-selected')).forEach((label) => {
      label.innerText = 'All';
    });

    // Close all filter dropdowns
    Array.from(this.querySelectorAll('.filter-box-container')).forEach((container) => {
      container.classList.remove('active');
    });

    Array.from(this.querySelectorAll('.filter-box-content')).forEach((content) => {
      content.classList.remove('active');
    });

    // Reset sort to default
    Array.from(this.querySelectorAll('.js-sort-filter')).forEach((sortItem) => {
      sortItem.classList.remove('active-filter');
      const checkbox = sortItem.querySelector('input[type="checkbox"]');
      if (checkbox) checkbox.checked = false;
    });

    // Set manual sort as active
    const manualSort = this.querySelector('.js-sort-filter[data-sort="manual"]');
    if (manualSort) {
      manualSort.classList.add('active-filter');
      const checkbox = manualSort.querySelector('input[type="checkbox"]');
      if (checkbox) checkbox.checked = true;
    }

    // Update sort label
    const sortLabel = this.querySelector('.filter-box-selected[data-category="Sort"]');
    if (sortLabel) {
      sortLabel.innerText = 'Recommended';
    }

    this.currentSort = 'manual';

    // Reset internal state
    this.activeFilters = {
      category: [],
      rooms: [],
      collections: [],
      size: [],
    };

    // document.querySelector('.filter-box-content').classList.remove('active');
    setTimeout(() => {
      // Scroll to top smoothly first
      document.body.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, '500');

    // Fetch and reload to show all products
    this.fetchAndReplaceHtml(true, true);
  }

  fetchFilters() {
    const roomsElement = this.querySelector('#Rooms');
    const categoryElement = this.querySelector('#Category');

    // Always fetch Rooms and Category filters from 'all' collection
    fetch('/collections/all')
      .then((response) => response.text())
      .then((allText) => {
        const allHtml = document.createElement('div');
        allHtml.innerHTML = allText;

        // Always fetch Rooms filter from 'all' collection
        if (roomsElement) {
          const allRoomFilter = allHtml.querySelector('#Rooms');
          if (allRoomFilter) {
            roomsElement.innerHTML = allRoomFilter.innerHTML;
          }
        }

        // Always fetch Category filter from 'all' collection
        if (categoryElement) {
          const allCategoryFilter = allHtml.querySelector('#Category');
          if (allCategoryFilter) {
            categoryElement.innerHTML = allCategoryFilter.innerHTML;
          }
        }

        this.handleFilterSelect();
        this.handleOpenFilterBox();
      });
  }

  handleUrlFilters() {
    const urlParams = new URLSearchParams(window.location.search);

    const roomParam = urlParams.get('room');
    if (roomParam) {
      const roomMap = {
        'living-room': 'Living Room',
        'dining-room': 'Dining Room',
        bedroom: 'Bedroom',
      };

      const roomValue = roomMap[roomParam];
      if (roomValue) {
        setTimeout(() => {
          this.setActiveRoomFilter(roomValue);
        }, 1000);
      }
    }
  }

  setActiveRoomFilter(roomValue) {
    // Remove active class from all room filters
    this.querySelectorAll('[data-category="Rooms"]').forEach((filter) => {
      filter.classList.remove('active-filter');
      const checkbox = filter.querySelector('input[type="checkbox"]');
      if (checkbox) checkbox.checked = false;
    });

    // Set the specific room as active
    const targetFilter = this.querySelector(`[data-category="Rooms"][data-value="${roomValue}"]`);
    if (targetFilter) {
      targetFilter.classList.add('active-filter');
      const checkbox = targetFilter.querySelector('input[type="checkbox"]');
      if (checkbox) checkbox.checked = true;

      // Update the activeFilters object
      this.activeFilters.rooms = [roomValue];

      // Trigger the filter update
      this.fetchAndReplaceHtml(true, true);
    }
  }

  fetchAndReplaceHtml(reloadFilters = false, resetPage = false) {
    if (resetPage) {
      this.currentPage = 1;
    }

    this.buildFetchURL();

    const path = `/collections/${this.collection}`;
    const queryString = this.paramURL ? `?${this.paramURL}` : '';
    const requestUrl = `${path}${queryString}`;

    fetch(requestUrl)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const incomingProductGridContainer = html.querySelector('#ProductGridContainer');

        if (!incomingProductGridContainer) {
          console.error('No product grid container found in response');
          return;
        }

        const productGridContainer = this.querySelector('#ProductGridContainer');
        if (!productGridContainer) {
          console.error('Product grid container missing in DOM');
          return;
        }

        productGridContainer.innerHTML = incomingProductGridContainer.innerHTML;

        const scrollTarget = this.closest('section-filters') || this;
        if (scrollTarget && typeof scrollTarget.scrollIntoView === 'function') {
          scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        const productGrid = productGridContainer.querySelector('#product-grid');
        const categoryFilters = productGrid ? productGrid.querySelectorAll('.product-info-json') : [];

        // REINITIALIZE CURSOR EFFECTS AFTER NEW CONTENT IS LOADED
        if (window.customHandleCursorEffect) {
          window.customHandleCursorEffect();
        }

        // Also reinitialize GSAP cursor if on desktop
        if (window.innerWidth >= 1280 && window.gsapCustomCursor) {
          window.gsapCustomCursor();
        }

        if (reloadFilters) {
          this.availableCategoryFilters(categoryFilters);
        }

        this.updateCurrentPageFromDom();
        this.updateBrowserHistory(queryString);
      })
      .catch((error) => {
        console.error('Error fetching products:', error);
      });
  }

  buildFetchURL() {
    const params = [];

    if (this.activeFilters.rooms.length) {
      this.activeFilters.rooms.forEach((room) => {
        if (room !== 'All') {
          params.push(`filter.p.m.custom.rooms=${encodeURIComponent(room)}`);
        }
      });
    }

    if (this.activeFilters.category.length) {
      this.activeFilters.category.forEach((category) => {
        if (category !== 'All') {
          const categoryFilterElement = this.querySelector(
            `.js-filter[data-category="Category"][data-value="${category}"]`
          );

          const categoryParamValue = categoryFilterElement?.dataset.label?.trim() || category;
          const encodedCategory = encodeURIComponent(categoryParamValue);

          params.push(`filter.p.m.custom.product_category=${encodedCategory}`);
        }
      });
    }

    if (this.activeFilters.size.length) {
      this.activeFilters.size.forEach((size) => {
        if (size !== 'All') {
          params.push(`filter.v.option.size=${encodeURIComponent(size)}`);
        }
      });
    }

    if (this.activeFilters.collections.length) {
      const lastCollection = this.activeFilters.collections[this.activeFilters.collections.length - 1];
      if (lastCollection !== 'All') {
        this.collection = lastCollection.replaceAll(' ', '-').toLowerCase();
      } else {
        this.collection = 'all';
      }
    } else {
      this.collection = 'all';
    }

    if (this.currentSort && this.currentSort !== 'manual') {
      params.push(`sort_by=${this.currentSort}`);
    }

    if (this.currentPage && this.currentPage > 1) {
      params.push(`page=${this.currentPage}`);
    }

    this.paramURL = params.join('&');
  }

  handlePaginationClick(event) {
    const paginationLink = event.target.closest('.js-pagination-link');
    if (!paginationLink || !this.contains(paginationLink)) {
      return;
    }

    event.preventDefault();

    const targetPage = parseInt(paginationLink.dataset.page, 10);
    if (Number.isNaN(targetPage) || targetPage < 1 || targetPage === this.currentPage) {
      return;
    }

    this.currentPage = targetPage;
    this.fetchAndReplaceHtml(false);
  }

  updateCurrentPageFromDom() {
    const activePageEl = this.querySelector('.pagination__link--active');
    if (activePageEl) {
      const parsed = parseInt(activePageEl.textContent.trim(), 10);
      if (!Number.isNaN(parsed)) {
        this.currentPage = parsed;
        return;
      }
    }

    this.currentPage = 1;
  }

  updateBrowserHistory(queryString) {
    try {
      const url = new URL(window.location.href);
      url.search = queryString;
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      console.warn('Unable to update browser history', error);
    }
  }
}

customElements.define('section-filters', filterContainer);

class productCard extends HTMLElement {
  constructor() {
    super();
    this.quantity = 1;
    this.quantityInput = this.querySelector('.js-quantity-input');
    this.handleQuantityChange();
    this.addToCart();
  }

  addToCart() {
    this.querySelector('.js-button-add')?.addEventListener('click', (event) => {
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: event.target.dataset.uid,
              quantity: this.quantity,
            },
          ],
        }),
      }).then((resp) => {
        if (resp.status == 200) {
          document.querySelector('.js-added-to-cart').classList.remove('hidden');

          // Refresh the cart modal to show the new item
          const cartModalElement = document.querySelector('cart-modal');
          if (cartModalElement && typeof cartModalElement.fetchProducts === 'function') {
            cartModalElement.fetchProducts();
          }

          setTimeout(() => {
            document.querySelector('.js-added-to-cart').classList.add('hidden');
          }, 2000);
        }
      });
    });
  }

  handleQuantityChange() {
    this.querySelector('.js-plus-quantity')?.addEventListener('click', () => {
      this.quantity++;
      this.quantityInput.value = this.quantity;
    });

    this.querySelector('.js-minus-quantity')?.addEventListener('click', () => {
      if (this.quantity - 1 != 0) {
        this.quantity--;
        this.quantityInput.value = this.quantity;
      }
    });
  }
}

customElements.define('product-card', productCard);

class cartModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.fetchProducts();
  }

  fetchProducts() {
    fetch(`/cart`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;

        this.innerHTML = html.querySelector('cart-modal').innerHTML;

        this.handleProductRemove();
        this.handleProductQuantity();
      });
  }

  handleProductQuantity() {
    Array.from(this.querySelectorAll('.js-minus-quantity')).map((element) => {
      element.addEventListener('click', (event) => {
        let updates = {};
        if (this.getProductQuantity(element) - 1 != 0) {
          updates[element.parentElement.dataset.product] = this.getProductQuantity(element) - 1;

          fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ updates }),
          }).then((resp) => {
            this.fetchProducts();
          });
        }
      });
    });

    Array.from(this.querySelectorAll('.js-plus-quantity')).map((element) => {
      element.addEventListener('click', (event) => {
        let updates = {};
        updates[element.parentElement.dataset.product] = this.getProductQuantity(element) + 1;

        fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        }).then((resp) => {
          this.fetchProducts();
        });
      });
    });
  }

  getProductQuantity(element) {
    return parseInt(element.parentElement.querySelector('.js-quantity-input').value);
  }

  handleProductRemove() {
    Array.from(this.querySelectorAll('.js-remove-product')).map((element) => {
      element.addEventListener('click', (event) => {
        let updates = {};

        updates[element.dataset.product] = 0;

        fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        }).then((resp) => {
          this.fetchProducts();
        });
      });
    });
  }
}

customElements.define('cart-modal', cartModal);

class registerUser extends HTMLElement {
  constructor() {
    super();
  }

  serialize(data) {
    let obj = {};
    for (let [key, value] of data) {
      if (obj[key] !== undefined) {
        if (!Array.isArray(obj[key])) {
          obj[key] = [obj[key]];
        }
        obj[key].push(value);
      } else {
        obj[key] = value;
      }
    }
    return obj;
  }

  connectedCallback() {
    // this.handleUserRegistration();
  }

  handleUserRegistration() {
    this.querySelector('.js-register-user').addEventListener('click', () => {
      const password = this.querySelector('.js-input-password').value;
      const email = this.querySelector('.js-input-email').value;

      fetch(window.Shopify.routes.root + 'account', {
        method: 'POST',
        body: JSON.stringify({
          'customer[email]': email,
          'customer[password]': password,
          form_type: 'create_customer',
        }),
      }).then((resp) => {
        console.log(resp);
      });
    });
  }
}

customElements.define('register-user', registerUser);

document.addEventListener('DOMContentLoaded', function () {
  const buttons = document.querySelectorAll('.scroll-text-button');

  buttons.forEach((button) => {
    const isScrollSpeedDynamic = button.dataset.speed === 'dynamic';
    const scrollSpeed = button.dataset.speed;
    const textSpan = button.querySelector('.button-text');
    const textContent = textSpan.innerText;

    textSpan.innerHTML = `<span class="scroll-content">${textContent} &#160; ${textContent} &#160; ${textContent} &#160; ${textContent}</span>`;

    const scrollContent = textSpan.querySelector('.scroll-content');
    scrollContent.style.display = 'inline-block';
    scrollContent.style.whiteSpace = 'nowrap';

    if (isScrollSpeedDynamic) {
      scrollContent.style.animation = `scrollText ${scrollSpeed}s linear infinite`;
    } else {
      scrollContent.style.animation = `scrollText ${scrollSpeed}s linear infinite`;
    }
  });

  const style = document.createElement('style');
  style.innerHTML = `
      @keyframes scrollText {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
      }
  `;
  document.head.appendChild(style);
});

document.addEventListener('DOMContentLoaded', function () {
  let buyInput = document.querySelector('.quantity-input');
  document.querySelectorAll('.qty-btn').forEach((btn) => {
    btn.addEventListener('click', function () {
      let input = this.parentNode.querySelector('.qty-input');
      let value = parseInt(input.value);
      if (this.classList.contains('plus')) {
        input.value = value + 1;
        buyInput.value = value + 1;
      } else if (this.classList.contains('minus') && value > 1) {
        input.value = value - 1;
        buyInput.value = value - 1;
      }
      if (input.value > 99) {
        input.style.maxWidth = '40px';
      } else {
        input.style.maxWidth = '30px';
      }
    });
  });
});

document.addEventListener('DOMContentLoaded', function () {
  const thumbnails = document.querySelectorAll('.product-thumbnail-list li');
  const sections = document.querySelectorAll("[id^='media-']");

  if (sections.length === 0 || thumbnails.length === 0) return; // Prevent running if no elements found

  thumbnails.forEach((thumbnail) => {
    thumbnail.addEventListener('click', function () {
      const mediaId = this.getAttribute('id');
      const targetElement = document.getElementById(mediaId.replace('nav-', ''));

      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  function highlightThumbnail() {
    let currentActive = null;

    sections.forEach((section, index) => {
      const rect = section.getBoundingClientRect();
      if (rect.top >= 0 && rect.top < window.innerHeight * 0.5) {
        currentActive = index;
      }
    });

    if (currentActive !== null && currentActive < thumbnails.length) {
      thumbnails.forEach((thumb) => thumb.classList.remove('active'));
      thumbnails[currentActive].classList.add('active');
    }
  }

  document.addEventListener('scroll', highlightThumbnail);
  // highlightThumbnail();
});

// if(window.innerWidth < 1025) {
//   let productGallery = document.querySelector('.product-gallery-parent ul');
//   if(productGallery) {
//     productGallery.classList.add('swiper-wrapper');
//   }
// }

class ProductSlider extends HTMLElement {
  constructor() {
    super();
    this.zIndexClass = 'z-40';
    const swiperElement = this.querySelector('.swiper');

    this.nextArrow = this.querySelector('.js-slider-next');
    this.prevArrow = this.querySelector('.js-slider-prev');

    if (!swiperElement) {
      return;
    }

    this.handleCarousel(swiperElement);
  }

  handleCarousel(swiperElement) {
    const swiper = new Swiper(swiperElement, {
      loop: false,
      slidesPerView: 1,
      centeredSlides: false,
      init: false,
      noSwiping: false,
      navigation: {
        nextEl: this.nextArrow || undefined,
        prevEl: this.prevArrow || undefined,
      },
    });

    if (window.innerWidth < 1025) {
      swiper.init();
      this.setupThumbnailNavigation(swiper);
    }

    swiper.on('slideChange', () => {
      this.updateActiveThumbnail(swiper);
    });
  }

  setupThumbnailNavigation(swiper) {
    const thumbnails = document.querySelectorAll('.product-thumbnails .swiper-slide');

    if (thumbnails.length === 0) {
      console.warn('Thumbnails not found');
      return;
    }

    thumbnails.forEach((button) => {
      button.addEventListener('click', () => {
        const slideIndex = parseInt(button.id.replace('media-nav-', '')) - 1;
        if (!isNaN(slideIndex)) {
          swiper.slideTo(slideIndex);
        }
      });
    });
  }

  updateActiveThumbnail(swiper) {
    const thumbnails = document.querySelectorAll('.product-thumbnails .swiper-slide');
    if (thumbnails.length === 0) return;

    thumbnails.forEach((slide) => slide.classList.remove('active'));

    const activeThumbnail = document.querySelector(`.product-thumbnails #media-nav-${swiper.activeIndex + 1}`);
    if (activeThumbnail) {
      activeThumbnail.classList.add('active');
    }
  }
}

customElements.define('product-gallery', ProductSlider);

class checkoutBusiness extends HTMLElement {
  constructor() {
    super();
    this.banner = this.querySelector('.js-checkout-banner');
  }

  connectedCallback() {
    this.querySelector('.js-submit-order').addEventListener('click', () => {
      const products = [];
      const order = {};

      let formValid = false;

      // Get Bold cart data if available for discounted prices
      let boldCart = null;
      try {
        boldCart =
          window.boldCartData ||
          (window.BOLD && window.BOLD.pre && typeof window.BOLD.pre.getCart === 'function'
            ? window.BOLD.pre.getCart()
            : null);
      } catch (e) {
        console.warn('Could not get Bold cart:', e);
      }

      Array.from(this.querySelectorAll('.js-checkout-product')).map((product) => {
        const productId = product.dataset.productId;
        const quantity = parseInt(product.dataset.quantity) || 1;

        const productData = {
          id: productId,
          quantity: quantity,
        };

        // If Bold cart is available, include discounted prices
        if (boldCart && boldCart.items && boldCart.items.length > 0) {
          // Find matching item in Bold cart (item.id format: "variantId:key")
          const boldItem = boldCart.items.find((item) => {
            const itemVariantId = item.id ? item.id.split(':')[0] : null;
            return (
              itemVariantId === productId || item.variant_id === productId || itemVariantId === productId.toString()
            );
          });

          if (boldItem) {
            // Get original unit price (from Shopify cart or Bold item)
            const originalUnitPrice = boldItem.original_price || boldItem.price || 0;

            // Get discounted unit price
            const discountedUnitPrice = boldItem.final_price || boldItem.price || originalUnitPrice;

            // Get line prices
            const originalLinePrice = boldItem.original_line_price || originalUnitPrice * quantity;
            const discountedLinePrice = boldItem.final_line_price || discountedUnitPrice * quantity;

            productData.originalUnitPrice = originalUnitPrice;
            productData.discountedUnitPrice = discountedUnitPrice;
            productData.originalLinePrice = originalLinePrice;
            productData.discountedLinePrice = discountedLinePrice;

            console.log(
              `Product ${productId}: Original £${originalUnitPrice / 100}, Discounted £${
                discountedUnitPrice / 100
              }, Quantity: ${quantity}`
            );
          } else {
            console.warn(`Could not find Bold item for product ${productId}`);
          }
        } else {
          console.warn('Bold cart not available, order will use full prices');
        }

        products.push(productData);
      });

      const formData = new FormData(this.querySelector('.form-order'));

      this.querySelector('.form-order').addEventListener('submit', (evt) => {
        evt.preventDefault();
      });

      for (const [key, value] of formData) {
        order[key] = value;
        if (!value) {
          formValid = false;
        } else {
          formValid = true;
        }
      }

      if (!formValid) {
        this.querySelector('.errors').classList.remove('hidden');
      } else {
        this.querySelector('.errors').classList.add('hidden');

        this.banner.setAttribute('style', 'display: block !important');

        setTimeout(() => {
          this.banner.setAttribute('style', 'display: none !important');
        }, 2500);
      }

      if (window.shopifyCustomer) {
        (async () => {
          // Add customer_id to order if missing
          if (!order.customer_id && window.shopifyCustomerId) {
            // Convert numeric ID to GID format if needed
            const customerId = window.shopifyCustomerId;
            order.customer_id = customerId.toString().startsWith('gid://')
              ? customerId
              : `gid://shopify/Customer/${customerId}`;
          }

          const requestBody = {
            customer: {
              email: window.shopifyCustomer,
            },
            products: products,
            order: order,
          };

          try {
            const rawResp = await fetch('https://kingsley-express-js-holy-dust-7900.fly.dev/', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(requestBody),
            });

            // Check if response is OK
            if (!rawResp.ok) {
              console.error('HTTP Error:', rawResp.status, rawResp.statusText);
              const errorText = await rawResp.text();
              console.error('Error response body:', errorText);
              return;
            }

            // Parse JSON response
            const responseData = await rawResp.json();

            fetch('/cart/clear', {
              method: 'POST',
            }).then((resp) => {
              console.log('Cart cleared');
            });
          } catch (error) {
            console.error('Error making request to Fly.io:', error);
            console.error('Error details:', error.message);
          }
        })();
      }
    });
  }
}

customElements.define('checkout-business', checkoutBusiness);

// make follower follow - implified with quickTo
gsap.set('.follower', {
  xPercent: -50,
  yPercent: -50,
});

let xTo = gsap.quickTo('.follower', 'x', {
    duration: 0.1,
    ease: 'power3',
  }),
  yTo = gsap.quickTo('.follower', 'y', {
    duration: 0.1,
    ease: 'power3',
  });

window.addEventListener('mousemove', (e) => {
  xTo(e.clientX);
  yTo(e.clientY);
});

//animate when passing over relevant objects
var followerIcon = document.querySelector('.follower__icon');
var followerText = document.querySelector('.follower__content');
let followerAnim = gsap.timeline({
  paused: true,
  overwrite: 'auto',
});
var followerContainer = document.querySelector('.follower');

followerAnim.to('.follower', {
  opacity: 1,
  scale: 1,
});

let cursorX = 0;
let cursorY = 0;

document.addEventListener('mousemove', (event) => {
  cursorX = event.clientX;
  cursorY = event.clientY;
});

let prevElement = null;
let scrollRafId = null;
let isScrolling = false;
let scrollTimeout = null;

document.addEventListener('scroll', () => {
  // Throttle using requestAnimationFrame to prevent layout thrashing
  if (scrollRafId) {
    cancelAnimationFrame(scrollRafId);
  }

  isScrolling = true;

  // Clear existing timeout
  if (scrollTimeout) {
    clearTimeout(scrollTimeout);
  }

  scrollRafId = requestAnimationFrame(() => {
    const hoveredElement = document.elementFromPoint(cursorX, cursorY);
    if (hoveredElement) {
      if (hoveredElement.closest('.followerchangetest')) {
        hoveredElement.closest('.followerchangetest')?.dispatchEvent(new Event('mouseenter'));
        prevElement = hoveredElement.closest('.followerchangetest');
      } else {
        prevElement?.dispatchEvent(new Event('mouseleave'));
        document.documentElement.style.cursor = 'auto';
      }
    }
    scrollRafId = null;
  });

  // Detect when scrolling has stopped
  scrollTimeout = setTimeout(() => {
    isScrolling = false;
  }, 150);
});

window.gsapCustomCursor = function () {
  // Remove existing event listeners first
  document.querySelectorAll('.followerchangetest').forEach((item) => {
    item.removeEventListener('mouseenter', handleGsapEnterDelegated, true);
    item.removeEventListener('mouseleave', handleGsapLeaveDelegated, true);
  });

  // Add new event listeners
  document.querySelectorAll('.followerchangetest').forEach((item) => {
    item.addEventListener('mouseenter', handleGsapEnterDelegated, true);
    item.addEventListener('mouseleave', handleGsapLeaveDelegated, true);
  });

  function handleGsapEnterDelegated(event) {
    const target = event.target.closest('.followerchangetest');
    if (!target) return;

    var text = target.dataset.followerText;
    var icon = target.dataset.followerIcon ? target.dataset.followerIcon.toLowerCase() : '';
    document.documentElement.style.cursor = 'none';

    if (icon == '') {
      return;
    }

    const elements = Array.from(document.querySelector('.cursor-overlay').children);
    elements.forEach((element) => {
      if (icon == element.dataset.cursorCategory || icon == '') {
        followerIcon.innerHTML = ''; // Clear previous content
        element.classList.remove('hidden');
        followerIcon.appendChild(element.cloneNode(true));
      }

      if (icon == 'prev' || icon == 'next') {
        followerContainer.classList.add('circle-follower');
      } else {
        followerContainer.classList.remove('circle-follower');
      }
    });

    if (text == 0) {
      text = '';
    }
    followerText.innerHTML = text;

    animateFollower('in');
  }

  function handleGsapLeaveDelegated(event) {
    const target = event.target.closest('.followerchangetest');
    if (!target) return;

    animateFollower('out');
    document.documentElement.style.cursor = 'auto';
  }
};

if (window.innerWidth >= 1280) {
  window.gsapCustomCursor();
}

function animateFollower(direction = 'in') {
  if (direction == 'in') {
    followerAnim.play().timeScale(1);
  } else {
    followerAnim.timeScale(-2);
  }
}

// document.addEventListener('DOMContentLoaded', function () {
//   const sectionFilters = document.querySelector('section-filters');
//   if (!sectionFilters) return;

//   const collectionTitle = sectionFilters.dataset.collection;
//   if (!collectionTitle) return;

//   function setActiveFilter(category, value) {
//     const categoryFilters = document.querySelectorAll(`[data-category="${category}"]`);
//     categoryFilters.forEach(filter => {
//       filter.classList.remove('active-filter');
//       const checkbox = filter.querySelector('input[type="checkbox"]');
//       if (checkbox) checkbox.checked = false;
//     });

//     const targetFilter = document.querySelector(`[data-category="${category}"][data-value="${value}"]`);
//     if (targetFilter) {
//       targetFilter.classList.add('active-filter');
//       const checkbox = targetFilter.querySelector('input[type="checkbox"]');
//       if (checkbox) checkbox.checked = true;
//     }
//   }

//   setTimeout(() => {
//     setActiveFilter('Category', collectionTitle);
//   }, 1000);
// });